import { useCallback, useEffect, useMemo, useState } from "react";
import { DateTime } from "luxon";
import type {
  TrainingEvent,
  TrainingAttemptThread,
  TrainingMessage,
} from "@/lib/api/training-simulator/training-simulator.ts";
import { TrainingSessionChat } from "./training-session-chat.tsx";
import { TrainingSessionProfilePanel } from "./training-session-profile-panel.tsx";
import {
  TrainingSessionThreadList,
  type TrainingThreadListItem,
} from "./training-session-thread-list.tsx";

interface TrainingSessionMessageGroup {
  thread: TrainingAttemptThread;
  messages: TrainingMessage[];
  events?: TrainingEvent[];
}

interface TrainingSessionWorkspaceProps {
  attemptId: string;
  threads: TrainingAttemptThread[];
  messageGroups: TrainingSessionMessageGroup[] | undefined;
  defaultThreadId?: string | null;
  isInProgress: boolean;
  isFinalizing?: boolean;
  replyFailedThreadIds?: ReadonlySet<string>;
  replyFailedPreview?: boolean;
  readOnly?: boolean;
  onSend?: (threadId: string | undefined) => void;
}

const SELECTED_THREAD_STORAGE_PREFIX = "training-simulator:selected-thread:";
const SEEN_THREADS_STORAGE_PREFIX = "training-simulator:seen-threads:";

type SeenThreadsById = Record<string, string>;

function getStoredThreadId(attemptId: string) {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem(
    `${SELECTED_THREAD_STORAGE_PREFIX}${attemptId}`,
  );
}

function storeThreadId(attemptId: string, threadId: string) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(
    `${SELECTED_THREAD_STORAGE_PREFIX}${attemptId}`,
    threadId,
  );
}

function getStoredSeenThreads(attemptId: string): SeenThreadsById {
  if (typeof window === "undefined") return {};

  const value = window.localStorage.getItem(
    `${SEEN_THREADS_STORAGE_PREFIX}${attemptId}`,
  );
  if (!value) return {};

  try {
    const parsed = JSON.parse(value);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {};
    }

    return Object.fromEntries(
      Object.entries(parsed).filter(
        (entry): entry is [string, string] => typeof entry[1] === "string",
      ),
    );
  } catch {
    return {};
  }
}

function storeSeenThreads(attemptId: string, seenThreads: SeenThreadsById) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(
    `${SEEN_THREADS_STORAGE_PREFIX}${attemptId}`,
    JSON.stringify(seenThreads),
  );
}

function getLatestMessage(messages: TrainingMessage[]) {
  return messages.reduce<TrainingMessage | null>((latest, message) => {
    if (!latest) return message;
    return DateTime.fromISO(message.created_at).toMillis() >
      DateTime.fromISO(latest.created_at).toMillis()
      ? message
      : latest;
  }, null);
}

function getLatestMessageCreatedAt(messages: TrainingMessage[]) {
  return getLatestMessage(messages)?.created_at ?? null;
}

function getUnreadTeenMessageCount(
  messages: TrainingMessage[],
  seenCreatedAt: string | undefined,
) {
  const seenAtMillis = seenCreatedAt
    ? DateTime.fromISO(seenCreatedAt).toMillis()
    : 0;

  return messages.filter(
    (message) =>
      message.sender_type === "simulated_teen" &&
      DateTime.fromISO(message.created_at).toMillis() > seenAtMillis,
  ).length;
}

export function TrainingSessionWorkspace({
  attemptId,
  threads,
  messageGroups,
  defaultThreadId,
  isInProgress,
  isFinalizing = false,
  replyFailedThreadIds,
  replyFailedPreview = false,
  readOnly = false,
  onSend,
}: TrainingSessionWorkspaceProps) {
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(() =>
    getStoredThreadId(attemptId),
  );
  const [seenThreadsById, setSeenThreadsById] = useState<SeenThreadsById>(() =>
    getStoredSeenThreads(attemptId),
  );

  useEffect(() => {
    setSelectedThreadId(getStoredThreadId(attemptId));
    setSeenThreadsById(getStoredSeenThreads(attemptId));
  }, [attemptId]);

  const hasSelectedThread = threads.some(
    (thread) => thread.id === selectedThreadId,
  );
  const activeThreadId =
    hasSelectedThread && selectedThreadId
      ? selectedThreadId
      : defaultThreadId ?? threads[0]?.id ?? undefined;

  const activeThread = threads.find((thread) => thread.id === activeThreadId);
  const activeReplyFailed =
    replyFailedPreview ||
    (activeThreadId
      ? replyFailedThreadIds?.has(activeThreadId) ?? false
      : false);

  const handleThreadSelect = useCallback(
    (threadId: string) => {
      setSelectedThreadId(threadId);
      storeThreadId(attemptId, threadId);
    },
    [attemptId],
  );

  const activeMessages = useMemo(
    () =>
      messageGroups?.find((group) => group.thread.id === activeThreadId)
        ?.messages ?? [],
    [messageGroups, activeThreadId],
  );

  const activeEvents = useMemo(
    () =>
      messageGroups?.find((group) => group.thread.id === activeThreadId)
        ?.events ?? [],
    [messageGroups, activeThreadId],
  );

  useEffect(() => {
    if (readOnly) return;
    if (!activeThreadId) return;

    const latestCreatedAt = getLatestMessageCreatedAt(activeMessages);
    if (!latestCreatedAt) return;

    setSeenThreadsById((seenThreads) => {
      if (seenThreads[activeThreadId] === latestCreatedAt) return seenThreads;

      const nextSeenThreads = {
        ...seenThreads,
        [activeThreadId]: latestCreatedAt,
      };
      storeSeenThreads(attemptId, nextSeenThreads);
      return nextSeenThreads;
    });
  }, [activeMessages, activeThreadId, attemptId, readOnly]);

  const unreadCountsByThreadId = useMemo(() => {
    if (readOnly) return {};
    if (!messageGroups) return {};

    return Object.fromEntries(
      messageGroups
        .map((group) => [
          group.thread.id,
          group.thread.id === activeThreadId
            ? 0
            : getUnreadTeenMessageCount(
                group.messages,
                seenThreadsById[group.thread.id],
              ),
        ])
        .filter(([, unreadCount]) => (unreadCount as number) > 0),
    );
  }, [activeThreadId, messageGroups, readOnly, seenThreadsById]);

  const listItems = useMemo<TrainingThreadListItem[]>(() => {
    const items = threads.map((thread) => {
      const messages =
        messageGroups?.find((group) => group.thread.id === thread.id)
          ?.messages ?? [];
      const latest = getLatestMessage(messages);
      const bucket =
        thread.conversation_bucket ??
        thread.level_teen?.conversation_bucket ??
        null;

      return {
        id: thread.id,
        name: thread.level_teen?.display_name ?? "Simulated teen",
        preview: latest?.content ?? "",
        lastMessageAt: latest?.created_at ?? thread.last_message_at,
        unreadCount: unreadCountsByThreadId[thread.id] ?? 0,
        bucket,
        online: bucket === "online",
      };
    });

    return items.sort((left, right) => {
      if (!left.lastMessageAt && !right.lastMessageAt) return 0;
      if (!left.lastMessageAt) return 1;
      if (!right.lastMessageAt) return -1;
      return (
        DateTime.fromISO(right.lastMessageAt).toMillis() -
        DateTime.fromISO(left.lastMessageAt).toMillis()
      );
    });
  }, [messageGroups, threads, unreadCountsByThreadId]);

  return (
    <div className="relative flex min-h-0 flex-1 overflow-hidden bg-surfaces-z3">
      <TrainingSessionThreadList
        key={attemptId}
        items={listItems}
        activeThreadId={activeThreadId}
        onSelect={handleThreadSelect}
      />
      <div className="flex min-h-0 min-w-0 flex-1 py-4 pl-2 pr-4">
        <div className="flex min-h-0 min-w-0 flex-1 overflow-hidden rounded-2xl bg-white shadow-[0_0_16px_rgb(0_0_0/0.08)]">
          <TrainingSessionChat
            attemptId={attemptId}
            thread={activeThread}
            messages={activeMessages}
            events={activeEvents}
            isInProgress={isInProgress}
            isFinalizing={isFinalizing}
            replyFailed={activeReplyFailed}
            readOnly={readOnly}
            onSend={() => onSend?.(activeThreadId)}
          />
          <TrainingSessionProfilePanel teen={activeThread?.level_teen} />
        </div>
      </div>
    </div>
  );
}
